// dadosDeCobranca.vm.js — CONTRATO da modal "Dados para o pagamento" (compra de pacote e de domínio).
// @version 2 (10/10/2026) — add: MEIO DE PAGAMENTO. Radio PIX × Cartão de crédito (só quando há mais de um
//             meio liberado), texto acima do botão que muda com o meio, e o rótulo do botão também.
//             Plano: docs/PLANO-CARTAO-PACOTES.md (TODO 2.1). A View fala em MEIO, nunca em gateway:
//             "ambiente do banco", não "Asaas" — trocar o gateway do cartão não mexe aqui.
// @version 1 (10/10/2026) — substitui o PackCpfDialog (pacote) e o CpfDialog do DomainsView (domínio):
//   CPF OU CNPJ (inclusive o CNPJ alfanumérico) + nome ou razão social. Plano:
//   docs/PLANO-DOCUMENTO-CPF-CNPJ-E-COBRANCA.md (TODO 3.3).
//
// A modal abre SEMPRE ao comprar, já preenchida com a cobrança salva (senão os dados da conta,
// senão em branco). Quem busca, mascara e valida é o hook useDadosDeCobranca — a View só exibe:
// `document` chega MASCARADO, erros e textos chegam prontos. A View não valida nada.

/**
 * @typedef {Object} MeioDePagamento
 * @property {'Pix'|'CreditCard'} id
 * @property {string} label            // "PIX" | "Cartão de crédito"
 */

/**
 * @typedef {Object} DadosDeCobrancaVM
 * @property {boolean} open
 * @property {boolean} loading          // buscando os dados salvos (campos desabilitados)
 * @property {boolean} busy             // gerando o pagamento (botões desabilitados, spinner no confirmar)
 * @property {string} title             // "Dados para o pagamento"
 * @property {string} explanation       // por que pedimos e onde muda depois
 * @property {string} name              // campo "Nome ou razão social"
 * @property {string|null} nameError
 * @property {string} document          // campo "CPF ou CNPJ", JÁ mascarado ("12.ABC.345/01DE-35")
 * @property {string|null} documentError
 * @property {string|null} formError    // erro devolvido pela API (fica na modal, o cliente corrige ali)
 * @property {MeioDePagamento[]} methods // meios liberados; o radio só aparece com 2+
 * @property {'Pix'|'CreditCard'} method // meio escolhido (padrão: Pix)
 * @property {string} methodHint        // texto ACIMA do botão — o que acontece ao confirmar, conforme o meio
 * @property {string} confirmLabel      // "Gerar QR Code" (PIX) | "Ir para o pagamento" (cartão)
 */

/**
 * @typedef {Object} DadosDeCobrancaActions
 * @property {(value: string) => void} onChangeName
 * @property {(value: string) => void} onChangeDocument   // o hook remascara a cada tecla
 * @property {(id: 'Pix'|'CreditCard') => void} onChangeMethod
 * @property {() => void} onConfirm
 * @property {() => void} onCancel                         // ignorado enquanto busy
 */

/** @typedef {{ vm: DadosDeCobrancaVM, actions: DadosDeCobrancaActions }} DadosDeCobrancaDialogProps */

export const DADOS_DE_COBRANCA_VM_VERSION = 2;

/** Rótulo e textos por meio — fonte única (hook, mocks). */
export const MEIOS_DE_PAGAMENTO = Object.freeze({
  Pix: Object.freeze({
    label: 'PIX',
    hint: 'Vamos exibir o QR Code e o código PIX (copia e cola) para você pagar.',
    confirmLabel: 'Gerar QR Code',
  }),
  CreditCard: Object.freeze({
    label: 'Cartão de crédito',
    hint: 'Você será redirecionado ao ambiente seguro do banco para pagar com o cartão. Ao terminar, volta para cá.',
    confirmLabel: 'Ir para o pagamento',
  }),
});

/**
 * Estado FECHADO, com todas as chaves do contrato. Fonte única para o hook (useDadosDeCobranca)
 * e para os mocks — duas cópias divergiriam.
 * @type {DadosDeCobrancaVM}
 */
export const DADOS_DE_COBRANCA_FECHADO = Object.freeze({
  open: false,
  loading: false,
  busy: false,
  title: 'Dados para o pagamento',
  explanation: 'Vão no comprovante do pagamento. Ficam salvos para as próximas compras — dá para mudar em Configurações.',
  name: '',
  nameError: null,
  document: '',
  documentError: null,
  formError: null,
  methods: Object.freeze([Object.freeze({ id: 'Pix', label: MEIOS_DE_PAGAMENTO.Pix.label })]),
  method: 'Pix',
  methodHint: MEIOS_DE_PAGAMENTO.Pix.hint,
  confirmLabel: MEIOS_DE_PAGAMENTO.Pix.confirmLabel,
});
